import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Gamepad2, User, LogOut, LayoutDashboard, Shield, Joystick } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { PUBLIC_CONTACT_EMAIL, PUBLIC_PRELAUNCH } from '../../config/publicSite';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const canUseApp = !PUBLIC_PRELAUNCH || user?.isAdmin;

  const isActive = (path: string) => location.pathname === path
    || (path === '/admin' && location.pathname.startsWith('/admin/'));
  const navigation = [
    { path: '/', label: t('nav.home'), icon: null },
    ...(canUseApp ? [{ path: '/games', label: t('navGames.games'), icon: Joystick }] : []),
    ...(user && canUseApp ? [{ path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard }] : []),
    ...(user?.isAdmin ? [{ path: '/admin', label: t('nav.admin'), icon: Shield }] : []),
  ];

  return (
    <nav className="bg-dark-900/80 backdrop-blur-md border-b border-dark-700 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 group" onClick={() => setIsOpen(false)}>
            <div className="w-9 h-9 bg-neon-500 rounded-lg flex items-center justify-center group-hover:shadow-neon transition-shadow">
              <Gamepad2 className="w-5 h-5 text-dark-950" aria-hidden="true" />
            </div>
            <span className="text-xl font-bold text-gray-100 leading-tight">Hostinator</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navigation.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                aria-current={isActive(path) ? 'page' : undefined}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  isActive(path)
                    ? 'text-neon-400 bg-neon-500/10'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-dark-700'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
                {label}
              </Link>
            ))}
            {PUBLIC_PRELAUNCH && (
              <a
                href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-dark-700 transition-colors"
              >
                {t('landing.contact')}
              </a>
            )}

            {user ? (
              <>
                <div className="w-px h-6 bg-dark-600 mx-2" />
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 rounded-lg">
                    <User className="w-4 h-4 text-gray-400" aria-hidden="true" />
                    <span className="text-sm text-gray-300 max-w-36 truncate">{user.name}</span>
                  </div>
                  <button
                    onClick={logout}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title={t('nav.logout')}
                    aria-label={t('nav.logout')}
                  >
                    <LogOut className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Link
                  to="/login"
                  className={PUBLIC_PRELAUNCH ? 'btn-secondary text-sm' : 'px-4 py-2 text-sm text-gray-300 hover:text-gray-100 transition-colors'}
                >
                  {t('nav.login')}
                </Link>
                {!PUBLIC_PRELAUNCH && (
                  <Link to="/register" className="btn-primary text-sm">
                    {t('nav.getStarted')}
                  </Link>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-200 hover:bg-dark-700 rounded-lg transition-colors"
            aria-label={t('landing.menuToggle')}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

        {isOpen && (
          <div id="mobile-navigation" className="lg:hidden border-t border-dark-700 py-4 space-y-1">
            {navigation.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                aria-current={isActive(path) ? 'page' : undefined}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(path) ? 'text-neon-400 bg-neon-500/10' : 'text-gray-400 hover:text-gray-200 hover:bg-dark-700'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {label}
              </Link>
            ))}
            {PUBLIC_PRELAUNCH && (
              <a
                href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
                className="block px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-dark-700 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {t('landing.contact')}
              </a>
            )}

            {user ? (
              <>
                <div className="border-t border-dark-700 my-3" />
                <div className="px-4 py-2">
                  <div className="flex items-center gap-2 text-gray-300 mb-3">
                    <User className="w-4 h-4" aria-hidden="true" />
                    {user.name}
                  </div>
                  <button
                    onClick={() => { logout(); setIsOpen(false); }}
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <LogOut className="w-4 h-4" aria-hidden="true" />
                    {t('nav.logout')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block px-4 py-2.5 text-gray-400 hover:text-gray-200 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.login')}
                </Link>
                {!PUBLIC_PRELAUNCH && (
                  <div className="px-4 pt-2">
                    <Link
                      to="/register"
                      className="btn-primary w-full justify-center"
                      onClick={() => setIsOpen(false)}
                    >
                      {t('nav.getStarted')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
